import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { ReactComponent as AddIcon } from '@/assets/icons/ri-add-line.svg';
import { ReactComponent as EditIcon } from '@/assets/icons/ri-edit-line.svg';
import Button from '@/components/common/Button';
import BreadcrumbLayout from '@/components/common/CustomLayout/BreadcrumbLayout';
import Modal from '@/components/common/Modal';
import Table from '@/components/common/Table';
import useCustomPaging from '@/hooks/useCustomPaging';
import useGenericSelectFilter from '@/hooks/useGenericSelectFilter';
import useSearchFilter from '@/hooks/useSearchFilter';
import { useAppDispatch, RootState } from '@/store';
import { getUserList, createUser, editUser } from '@/store/user/action';
import { getRoleList } from '@/store/role/action';
import { dynamicAttr } from '@/utils/fallback';
import { constructTableMetaFromPaging2 } from '@/utils/paging';
import { renderUserFormFooter, renderUserFormBody, renderUserFormHeader } from '@/pages/Users/Modal';
import GenericSelect from "@/components/common/GenericSelect";

const roleData = roles => (
  roles.map(role => ({
    label: role.name,
    value: role.id
  }))
)

const renderUserPageHeader = (
  searchString: string,
  handleChangeSearchString: any,
  handleSubmitSearch: any,
  showModal: Function
) => (
  <BreadcrumbLayout>
    <div className="flex justify-between items-center gap-8 p-0">
      <p className="font-bold text-2xl text-black-primary">Users</p>
      <div className="flex items-center gap-4">
        <input
          value={searchString}
          onChange={handleChangeSearchString}
          onKeyDown={handleSubmitSearch}
          name="searchString"
          type="text"
          className="flex items-center py-[8px] px-4 gap-2 border rounded border-solid border-stroke-subtle text-jsm font-normal"
          placeholder="Search for Users"
        />
        <div className="bg-grey-line-subtle h-6 w-1"/>
        <Button type="submit" variant="primary" className="text-white" onClick={() => showModal()}>
          <AddIcon/>
          <p>User</p>
        </Button>
      </div>
    </div>
  </BreadcrumbLayout>
)

const renderUserPageFilterSection = (
  selectedRole: { label: string, value: string },
  selectedRoleOptions: { label: string, value: string }[],
  setSelectedRole: any
) => (
  <div className="flex justify-between items-center p-4 p-6 bg-white border border-grey-line-subtle border-b-0 rounded-t">
    <div className="flex items-center p-0 gap-4">
      <GenericSelect
        className="min-w-[328px]"
        options={selectedRoleOptions}
        value={selectedRole}
        placeholder='Select Role'
        onChange={setSelectedRole}
      />
    </div>
    <div className="flex items-center p-0 gap-2">
      {/* TODO: To dropdown and do actual pagination */}
      10 Item Per Page
    </div>
  </div>
)

const HEADERS: TableHeader[] = [
  {
    label: 'Name',
    key: 'name',
  },
  {
    label: 'No. HP',
    key: 'phone_number',
  },
  {
    label: 'Email',
    key: 'email',
  },
  {
    label: 'Role',
    key: 'role'
  },
  {
    label: 'Action',
    key: 'action',
  }
]
const constructTableData = (data, showModal) => {
  const tableBody = data.map(user => {
    const {
      id,
      name,
      email,
      phone_number,
      role
    } = user
    return {
      id,
      value:
        {
          name,
          phone_number,
          email,
          role: {
            value: role.id,
            label: role.name
          },
          action: id
        }
    }
  })
  const component = {
    name: (v) => (<div className="flex items-center h-full font-bold text-sm">{v}</div>),
    phone_number: (v) => (<div className="flex items-center h-full text-sm">{v}</div>),
    email: (v) => (<div className="flex items-center h-full text-sm">{v}</div>),
    role: (v) => (<div className="flex items-center h-full font-bold text-sm">{v.label}</div>),
    action: (v: string) => (<div>
      <Button
        className="border border-grey-line-subtle"
        type='button'
        variant='basic'
        onClick={() => {
          showModal(v);
        }}>
        <EditIcon/>
        Edit
      </Button>
    </div>),
  }
  return {
    data: tableBody,
    component
  }
}
const SORT: TableSort = {
  type: 'SERVER',
  handler: () => {},
};


const Users = () => {
  const userMethods = useForm()
  const userState = useSelector((state: RootState) => state.user.state.items)
  const roleState = useSelector((state: RootState) => state.roles.state.items)
  const paging = useSelector((state: RootState) => state.user.state.paging)
  const [show, setShow] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState('')
  const dispatch = useAppDispatch()
  const [searchParams,] = useSearchParams()
  const [currentPageFilter,,handlePageNumberChange] = useCustomPaging()
  const [
    searchString,
    currentSearchFilter,
    handleChangeSearchString,
    handleSubmitSearch
  ] = useSearchFilter()
  const [
    currentRoleFilter,
    selectedRole,
    selectRoleOptions,
    handleChangeRole,
  ] = useGenericSelectFilter('role', roleData(roleState))
  const userMeta: UserFilter = {
    role: searchParams.get('roleId'),
    search: searchParams.get('search')
  }

  const showModal = (v?: string) => {
    if (v) {
      setSelectedUserId(v)
    }
    setShow(true)
  }
  const closeModal = () => {
    setSelectedUserId('')
    setShow(false)
  }
  const submitModal = async () => {
    const userData = userMethods.getValues()
    const data = {
      "name": userData.userName,
      "email": userData.email,
      "phone_number": userData.phoneNumber,
      "role_id": userData.role?.value
    }
    if (!selectedUserId) {
      await dispatch(createUser(data))
      await dispatch(getUserList(userMeta))
    }
    else {
      await dispatch(editUser({data, id: selectedUserId}))
      await dispatch(getUserList(userMeta))
    }
    closeModal()
  }

  const userData = (id: string) => constructTableData(userState, () => {}).data.find(row => row.value.action === id).value
  const setSortingParam = (key, order) => order === 'ASC' ? key : `-${key}`
  const getAllUsers = () => {
    const params = {
      size: 10,
      ...dynamicAttr('page', currentPageFilter),
      ...dynamicAttr('search', currentSearchFilter),
      ...dynamicAttr('role', currentRoleFilter),
      ...dynamicAttr('sort', setSortingParam(searchParams.get('sortBy'), searchParams.get('order'))),
    }
    dispatch(getUserList(params))
  }

  useEffect(() => {
    (async () => {
      dispatch(getRoleList({}))
    })()
  }, [])
  useEffect(() => {
    (async () => {
      await getAllUsers()
    })()
  }, [
    currentRoleFilter,
    currentPageFilter,
    currentSearchFilter,
    searchParams.get('sortBy'),
    searchParams.get('order')
  ])

  useEffect(() => {
    if (selectedUserId) {
      const user = userData(selectedUserId)
      const initialUser = {
        userName: user.name,
        phoneNumber: user.phone_number,
        email: user.email,
        role: { label: user.role.label, value: user.role.value }
      }
      userMethods.reset({...initialUser})
    } else { userMethods.reset({}) }
  }, [userMethods.reset, selectedUserId])

  return (
    <div className='flex flex-col w-full'>
      {renderUserPageHeader(
        searchString,
        handleChangeSearchString,
        handleSubmitSearch,
        showModal
      )}
      <div className='flex flex-col p-4 w-full'>
        {renderUserPageFilterSection(
          selectedRole,
          selectRoleOptions,
          handleChangeRole
        )}
        <Table headers={HEADERS}
                sort={SORT}
                body={constructTableData(userState, showModal)}
                meta={constructTableMetaFromPaging2(paging)}
                pageNumberHandler={handlePageNumberChange}/>
      </div>
      { show && <Modal show={show}
											 header={renderUserFormHeader}
											 body={renderUserFormBody({ userMethods }, selectedUserId, roleData(roleState), () => submitModal)}
											 footer={renderUserFormFooter(() => closeModal, () => submitModal)}
											 onClick={() => closeModal}
											 rootId="portal-user-form"/> }
    </div>
  );
}

export default Users
